import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useLatest } from "hooks/useLatest";
import { resolveSetStateAction } from "./utils";
import type { UrlStateOptions, SetValue } from "./types";

export const useSearchParamState = <T>({
  key,
  defaultValue,
  parse,
  serialize,
  resetParams,
  replace,
}: UrlStateOptions<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const latestOptionsRef = useLatest({
    key,
    defaultValue,
    parse,
    serialize,
    resetParams,
    replace,
  });

  const value = useMemo(() => {
    const { key, defaultValue, parse } = latestOptionsRef.current;
    const raw = searchParams.get(key);
    return raw === null ? defaultValue : parse(raw);
  }, [searchParams, latestOptionsRef]);

  const setValue: SetValue<T> = useCallback(
    (next, extraParams) => {
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          const { key, defaultValue, parse, serialize, resetParams } =
            latestOptionsRef.current;

          const rawPrev = nextParams.get(key);
          const prevValue = rawPrev === null ? defaultValue : parse(rawPrev);

          const resolved = resolveSetStateAction(next, prevValue);

          if (resolved === defaultValue) {
            nextParams.delete(key);
          } else {
            const raw = serialize(resolved);
            if (raw === "") nextParams.delete(key);
            else nextParams.set(key, raw);
          }

          const paramsToSet = { ...resetParams, ...extraParams };

          Object.entries(paramsToSet).forEach(([resetKey, resetValue]) => {
            if (resetValue === "") {
              nextParams.delete(resetKey);
            } else {
              nextParams.set(resetKey, resetValue);
            }
          });

          return nextParams;
        },
        { replace: latestOptionsRef.current.replace },
      );
    },
    [setSearchParams, latestOptionsRef],
  );

  return [value, setValue] as const;
};
